import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { Table } from "@tanstack/react-table";
import { FilterIcon } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

import { Button } from "../button";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../dropdown-menu";
interface DataTableViewOptionsProps<TData> {
    table: Table<TData>;
}

// Helper function to get translated column labels
const getColumnLabel = (columnId: string): string => {
    const labelMap: Record<string, string> = {
        // Specific to settings promotions
        "Customers": t`Customers`,
        "Promotion Name": t`Promotion Name`,
        "First Time Customers": t`First Time Customers`,
        "Gross Sales": t`Gross Sales`,
        "Net Sales": t`Net Sales`,
        "Average Order Size": t`Average Order Size`,
        "Discounts": t`Discounts`,
        "Uses": t`Uses`,
        "Status": t`Status`,
        //Specific to cash report
        "time": t`Time`,
        "employee": t`Employee`,
        "reason": t`Reason`,
        "purchase ID": t`Purchase ID`,
        "paid in": t`Paid In`,
        "paid out": t`Paid Out`,
        "amount": t`Amount`,
        //Specific to customer report
        "Name": t`Name`,
        "Lifetime Rank": t`Lifetime Rank`,
        "Lifetime Spent": t`Lifetime Spent`,
        "lifetimeOrders": t`Lifetime Orders`,
        "Customer Since": t`Customer Since`,
        "Last Order": t`Last Order`,
        "Frequency": t`Frequency`,
        "totalSpent": t`Total Spent`,
        "totalOrders": t`Total Orders`,
        "appOrders": t`App Orders`,
        "kioskOrders": t`Kiosk Orders`,
        "onlineOrders": t`Online Orders`,
        "Phone Number": t`Phone Number`,
        "email": t`Email`,
        //Specific to ambassador report
        "Ambassador Name": t`Ambassador Name`,
        "User Code": t`User Code`,
        "Number of Users": t`Number of Users`,
        "Number of Referrals": t`Number of Referrals`,
        "Attributed Net Sales": t`Attributed Net Sales`,
        //Specific to devices
        "Device Type": t`Device Type`,
        "Last Active": t`Last Active`,
        "Installed": t`Installed`,
        //Specific to invoices
        "Invoice Number": t`Invoice Number`,
        "Invoice Sent At": t`Invoice Sent At`,
        "Total Amount": t`Total Amount`,
        "Customer Name": t`Customer Name`,
        "Customer Email": t`Customer Email`,
        "Customer Paid At": t`Customer Paid At`,
        "Payout Processed At": t`Payout Processed At`,
        //Specific to timecards
        "Identifier": t`Identifier`,
        "Role": t`Role`,
        "Wage": t`Wage`,
        "Shifts": t`Shifts`,
        "Regular": t`Regular`,
        "Overtime": t`Overtime`,
        "Double Time": t`Double Time`,
        "Unpaid Breaks": t`Unpaid Breaks`,
        "Total Hours": t`Total Hours`,
        "Estimated Wages": t`Estimated Wages`,
        //Specific to tip reporting
        "hour": t`Hour`,
        "tips": t`Tips`,
        //Specific to sms campaigns
        "campaign": t`Campaign`,
        "segment": t`Segment`,
        "messagesSent": t`Messages Sent`,
        "attributedSales": t`Attributed Sales`,
        //Specific to guestbook
        "name": t`Name`,
        "credit": t`Store Credit`,
        "points Balance": t`Points Balance`,
        //Specific to promotions
        "promoName": t`Promotion Name`,
        "discount": t`Discount`,
        "start": t`Start`,
        "end": t`End`,
        "active": t`Active`,
        //Specific to orders
        "details": t`Details`,
        "orderNumber": t`Order #`,
        "dateReceived": t`Ordered At`,
        "status": t`Status`,
        "customerName": t`Customer`,
        "tableNumber": t`Table`,
        "employeeName": t`Employee`,
        "items": t`Items`,
        "promotions": t`Promotions`,
        "completionTime": t`Completion Time`,
        "notes": t`Notes`,
        "scheduledFor": t`Scheduled`,
        "fulfillment": t`Fulfillment`,
        "channel": t`Channel`,
        "provider": t`Provider`,
        "subtotal": t`Subtotal`,
        "customSurcharge": t`Custom Surcharge`,
        "customDiscounts": t`Custom Discounts`,
        "upCharge": t`Up Charge`,
        "deliveryCharge": t`Delivery Charge`,
        "thirdPartyDeliveryCharge": t`3P Delivery Charge`,
        "snackpassFee": t`Snackpass Fee`,
        "processingFee": t`Processing Fee`,
        "thirdPartyFees": t`Estimated Third Party Fees`,
        "customerToStoreFees": t`Cust. To Store Fees`,
        "taxesYouOwe": t`Tax`,
        "taxesRemitted": t`Taxes Remitted On Your Behalf`,
        "thirdPartyTaxes": t`Estimated Third-Party Taxes`,
        "paymentMethod": t`Payment Method`,
        "giftCardCredit": t`Gift Card Redemp.`,
        "storeCredit": t`Store Credit Redemp.`,
        "tip": t`Tips`,
        "total": t`Total`,
        "netSales": t`Net Sales`,
        "grossSales": t`Gross Sales`,
        "thirdPartyPayout": t`Estimated Third-Party Payout`,
        "expectedCash": t`Cash`,
        "refundedBy": t`Refunded By`,
        "refundedAmount": t`Refunded Amount`,
        "upChargedBy": t`Up-Charged By`,
        "cashAcceptedBy": t`Cash Accepted By`,
        "createdBy": t`Created By`,
        "id": t`ID`,
        "payoutId": t`Payout ID`,
        "paidOutOn": t`Paid Out date`,
        "actualPayout": t`Paid Out Amount`,
        "receiptNumber": t`Legacy Receipt Number`,
        //Specific to audit log
        "Action": t`Action`,
        "Edited By": t`Edited By`,
        "Time Modified": t`Time Modified`,
        //Specific to Balances
        "Store Name": t`Store Name`,
        "Purchase Date": t`Purchase Date`,
        "Gift Card Type": t`Gift Card Type`,
        "Code": t`Code`,
        "First Use Date": t`First Use Date`,
        "Last Use Date": t`Last Use Date`,
        "Purchase Amount": t`Purchase Amount`,
        "Current Balance": t`Current Balance`,
        "From": t`From`,
        "From Email": t`From Email`,
        "To": t`To`,
        "To Email": t`To Email`,
        "To Phone": t`To Phone`,
        //Specific to gift card transactions
        "Date": t`Date`,
        "Type": t`Type`,
        "Transaction Amount": t`Transaction Amount`,
        "Balance": t`Balance`,
        "Purchase Customer": t`Purchase Customer`,
        "Usage Customer": t`Usage Customer`,
        "Gift Card Purchased Via": t`Gift Card Purchased Via`,
        "activity": t`Activity`,
        //Specific to liabilities
        "payable": t`Payable`,
        "receivable": t`Receivable`,
        "net": t`Net Receivable`,
        //Specific to reports
        "date": t`Date`,
        "orders": t`Orders`,
        "refundTotal": t`Refund Total`,
        "purchaseDiscount": t`Purchase Discount`,
        "giftCardsCredit": t`Gift Cards Credit`,
        "storeTaxesToRemit": t`Store Taxes To Remit`,
        "totalSales": t`Total Sales`,
        "processingFees": t`Processing Fees`,
        "snackpassFees": t`Snackpass Fees`,
        "tipPercentage": t`Tip Percentage`,

        // Specific to sensitive actions table
        "actionType": t`Action`,
        "purchaseCreationTimestampLocal": t`Time`,
        "actionTimestampLocal": t`Time`,
    };
    
    return labelMap[columnId] || columnId;
};

export function DataTableViewOptions<TData>({
    table,
}: DataTableViewOptionsProps<TData>) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto h-8 flex"
                >
                    <FilterIcon className="h-4 w-4" />
                    <span className="hidden md:block ml-2"><Trans>Columns</Trans></span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="max-h-[300px] overflow-y-auto"
            >
                <DropdownMenuLabel><Trans>Toggle columns</Trans></DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                    .getAllColumns()
                    .filter(
                        (column) =>
                            typeof column.accessorFn !== "undefined" &&
                            column.getCanHide(),
                    )
                    .map((column) => (
                        <DropdownMenuCheckboxItem
                            key={column.id}
                            className="capitalize"
                            checked={column.getIsVisible()}
                            onCheckedChange={(value) =>
                                column.toggleVisibility(!!value)
                            }
                            onSelect={(e) => e.preventDefault()}
                        >
                            {getColumnLabel(column.id)}
                        </DropdownMenuCheckboxItem>
                    ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}